import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, Sparkles } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import ServicesCarousel from '../components/ServicesCarousel';
import { obtenerCarrusel } from '../api/carrusel';
import { obtenerProductos } from '../api/productos';
import { useNotify } from '../components/NotificationProvider';
import { buildServiceCategories, normalizeCategoryKey } from '../utils/helpers';

// Página pública de productos (solo lectura)
const PublicProducts = () => {
  const [products, setProducts] = useState([]);
  const [carouselImages, setCarouselImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { info: notifyInfo } = useNotify();

  useEffect(() => {
    obtenerCarrusel()
      .then((data) => setCarouselImages(Array.isArray(data) ? data : []))
      .catch(() => setCarouselImages([]));

    // Renderiza inmediatamente desde caché para evitar “re-carga” visual al volver
    try {
      const raw = localStorage.getItem('productsCache');
      if (raw) {
        const cache = JSON.parse(raw);
        if (Array.isArray(cache.items)) {
          setProducts(cache.items);
          setLoading(false);
        }
      }
    } catch {}
    const fetchData = async () => {
      try {
        const data = await obtenerProductos();
        // Mostrar solo disponibles (Disponible === 1) si el campo existe
        const visibles = Array.isArray(data)
          ? data.filter(p => p.Disponible === 1 || p.Disponible === true || p.disponible === 1)
          : [];
        setProducts(visibles);
        // Guarda caché ligera para próximas visitas
        try { localStorage.setItem('productsCache', JSON.stringify({ ts: Date.now(), items: visibles })); } catch {}
      } catch (e) {
        setError(e.message || 'Error cargando productos');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="py-20 text-center text-gray-600">Cargando productos...</div>;
  if (error) return <div className="py-20 text-center text-red-500">{error}</div>;

  const categories = buildServiceCategories(products);

  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      (product.Nombre || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (product.Descripcion || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchesFilter =
      filter === 'all' ||
      normalizeCategoryKey(product.Categoria || product.CategoriaTexto) === filter;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      <h1 className="text-4xl md:text-5xl font-extrabold mb-3 text-center bg-clip-text text-transparent bg-gradient-to-r from-pink-500 via-purple-600 to-indigo-600 drop-shadow-sm">
        Nuestros Servicios
      </h1>
      <div className="flex justify-center mb-6">
        <span className="inline-block h-[3px] w-24 md:w-32 rounded-full bg-gradient-to-r from-pink-400 via-purple-500 to-indigo-500" />
      </div>
      <ServicesCarousel images={carouselImages} />
      <p className="text-gray-700 mb-10 max-w-2xl mx-auto text-center leading-relaxed">
        Explora parte del catálogo disponible.
        {' '}
        <button
          type="button"
          className="font-bold text-purple-700 hover:text-purple-800"
          onClick={() => { try { window.dispatchEvent(new CustomEvent('open-login')); } catch {} }}
        >
          Inicia sesión
        </button>
        {' '}o{' '}
        <button
          type="button"
          className="font-bold text-pink-600 hover:text-pink-700"
          onClick={() => navigate('/register', { replace: false, state: { from: '/productos' } })}
        >
          crea tu cuenta
        </button>
        {' '}para reservar y ver más detalles personalizados.
      </p>

      <motion.div
        className="mx-auto mb-8 flex w-full max-w-6xl gap-3 overflow-x-auto px-4 pb-2 sm:flex-wrap sm:justify-center sm:overflow-visible"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {categories.map((category, index) => (
          <motion.button
            key={category.key}
            onClick={() => setFilter(category.key)}
            className={`flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-5 py-3 font-medium transition-all duration-300 ${
              filter === category.key
                ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                : 'bg-white/80 text-gray-700 hover:bg-white hover:shadow-md'
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 + 0.3 }}
          >
            <Filter className="w-4 h-4" />
            {category.label}
          </motion.button>
        ))}
      </motion.div>

      <motion.div
        className="mb-8 flex justify-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <input
          type="text"
          placeholder="Buscar servicio..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full max-w-md px-6 py-3 rounded-2xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none"
        />
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredProducts.map((product, index) => (
          <ProductCard
            key={product.Id || index}
            product={product}
            isAdmin={false}
            index={index}
            onSelect={(p) => {
              const hasUser = !!localStorage.getItem('user');
              if (!hasUser) {
                    const id = notifyInfo('Inicia sesión o crea tu cuenta para reservar', { duration: 5000 });
                    try { window.lastLoginPromptId = id; } catch {}
                try {
                  localStorage.setItem('preselectProduct', JSON.stringify(p));
                } catch {}
                // Pedir login sin cambiar de ruta ni recargar lista
                try { window.dispatchEvent(new CustomEvent('open-login')); } catch {}
                return;
              }
              // Con sesión, llevar al flujo normal
              navigate('/', { replace: false, state: { selectProduct: p, requireLogin: false } });
            }}
          />
        ))}
      </div>
      {filteredProducts.length === 0 && (
        <motion.div
          className="text-center py-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-600 mb-2">
            No hay servicios disponibles
          </h3>
          <p className="text-gray-500">
            Intenta con otra categoría o vuelve más tarde.
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default PublicProducts;
